import { EnvironmentInjector, PendingTasks, inject, runInInjectionContext } from '@angular/core';
import { pendingUntilEvent } from '@angular/core/rxjs-interop';
import { Observable, asyncScheduler, observeOn, queueScheduler, subscribeOn } from 'rxjs';

type AnyFn = (...args: never[]) => unknown;

let warnedOutOfContext = false;
function warnOutOfContext(fnName: string): void {
  if (warnedOutOfContext) return;
  warnedOutOfContext = true;
  // eslint-disable-next-line no-console
  console.warn(
    `[@fido/firebase] ${fnName}() called outside an injection context — falling back to bare invocation. ` +
      `SSR pending-task tracking is skipped for this call. Same warning as @angular/fire: ` +
      `https://github.com/angular/angularfire/blob/main/docs/zones.md`,
  );
}

/**
 * Wraps a Firebase / rxfire function so its observable / promise / callback
 * outputs participate in Angular's PendingTasks system (SSR correctness)
 * AND are run inside the captured injection context (so callbacks can
 * call inject()).
 *
 * Ported from @angular/fire's ɵzoneWrap helper, stripped of Zone.js
 * plumbing because this app is zoneless via provideZonelessChangeDetection.
 *
 * Uses a Proxy so the wrapped function preserves the original type T —
 * generics and overloads on `fn` remain visible to consumers without
 * needing per-helper type re-declarations.
 *
 * @param fn - the rxfire / firebase function to wrap
 * @param blockUntilFirst - when true, observable subscriptions hold a
 *   PendingTask until the first emission. Use `true` for streaming helpers
 *   (collectionData, authState); `false` for one-shot getX functions.
 *
 * Mirrors @angular/fire's try/catch around inject() (see angular-fire.mjs's
 * ɵzoneWrap): if invoked outside an injection context — e.g. inside
 * rxResource's `stream:` factory which runs under `untracked()` — warns
 * once in dev and falls back to invoking the underlying function directly.
 * That call loses SSR pending-task tracking but stays functional.
 */
export function kdvWrap<T extends AnyFn>(fn: T, blockUntilFirst: boolean): T {
  return new Proxy(fn, {
    apply(target, thisArg, args) {
      let injector: EnvironmentInjector;
      let pendingTasks: PendingTasks;
      try {
        injector = inject(EnvironmentInjector);
        pendingTasks = inject(PendingTasks);
      } catch {
        warnOutOfContext(target.name);
        return Reflect.apply(target, thisArg, args);
      }
      let taskDone: (() => void) | undefined;

      // For callback-style APIs (e.g. onSnapshot(ref, next, error)), wrap each
      // function arg so it runs in the injection context. If blockUntilFirst,
      // hold a pending task until the first callback fires.
      for (let i = 0; i < args.length; i++) {
        const arg = args[i];
        if (typeof arg !== 'function') continue;
        if (blockUntilFirst) taskDone ||= pendingTasks.add();
        args[i] = function wrappedArg(...inner: unknown[]): unknown {
          if (taskDone) {
            // first call: schedule task removal on next microtask
            queueMicrotask(taskDone);
            taskDone = undefined;
          }
          return runInInjectionContext(injector, () => arg(...inner));
        };
      }

      const ret = Reflect.apply(target, thisArg, args);

      if (!blockUntilFirst) {
        return ret;
      }

      if (ret instanceof Observable) {
        // Mirrors @angular/fire's ɵzoneWrap behavior: wrap Observable returns
        // with subscribeOn + observeOn schedulers so emissions are NEVER
        // synchronous. Upstream's exact comment from ɵZoneScheduler:
        // "This scheduler needs to schedule asynchronously always to ensure
        // that firebase emissions are never synchronous."
        //
        // This matters for the auth-flip race on signOut: Firebase's
        // notifyAuthListeners fires all listeners synchronously in
        // registration order. Without async scheduling, our rxfire user()
        // emission propagates synchronously to a stale rxResource subscription
        // before its teardown can run, leaving Firestore's later listener to
        // restart watch streams under no-auth and surface security-rule
        // denials through the still-attached subscription. With async
        // scheduling, both our emission and Firestore's restart land in the
        // task queue; the rxResource teardown completes before the error
        // propagates.
        return ret.pipe(
          subscribeOn(queueScheduler),
          observeOn(asyncScheduler),
          pendingUntilEvent(injector),
        );
      }

      if (ret instanceof Promise) {
        const removeTask = pendingTasks.add();
        ret.finally(removeTask);
        return ret;
      }

      // Unsubscribe-function returns from listener APIs.
      if (typeof ret === 'function' && taskDone) {
        const wrappedTaskDone = taskDone;
        taskDone = undefined;
        return function teardown(this: unknown, ...inner: unknown[]): unknown {
          queueMicrotask(wrappedTaskDone);
          return Reflect.apply(ret, this, inner);
        };
      }

      return ret;
    },
  });
}
